/**
 * Token types produced by `parseTextWithTags`.
 *
 * - `TextToken`: a plain text run
 * - `BreakLineToken`: a line break (from `\n` in the source string)
 * - `ElementToken`: a structural element whose ID and inner tokens are preserved
 */
export type TextToken = {type: 'text'; value: string};
export type BreakLineToken = {type: 'breakline'};
export type ElementToken = {type: 'element'; id: string; children: Token[]};
export type Token = TextToken | BreakLineToken | ElementToken;

/**
 * Parses a string that may contain `<n id>…</n>` structural tags into a token tree.
 *
 * The tag format is produced by `collectTextFromDescendantsWithXmlWrapping` during the
 * collection phase and may also appear in OpenAI tag-relocation output. The parser is
 * intentionally lenient: unclosed or malformed tags are treated as plain text so that
 * a bad OpenAI response never corrupts the translated result.
 *
 * @example
 * parseTextWithTags('Hello <n abc>world</n>!')
 * // [
 * //   { type: 'text', value: 'Hello ' },
 * //   { type: 'element', id: 'abc', children: [{ type: 'text', value: 'world' }] },
 * //   { type: 'text', value: '!' },
 * // ]
 */
export function parseTextWithTags(text: string): Token[] {
  const roots: Token[] = [];
  // Stack tracks open ElementTokens so we can nest children correctly.
  const stack: ElementToken[] = [];
  let position = 0;

  const currentChildren = (): Token[] => stack.at(-1)?.children ?? roots;

  while (position < text.length) {
    const remaining = text.slice(position);

    // Opening tag: <n id>
    const openMatch = remaining.match(/^<n ([^>]+)>/);
    if (openMatch?.[1]) {
      const element: ElementToken = {
        type: 'element',
        id: openMatch[1],
        children: [],
      };
      stack.push(element);
      position += openMatch[0].length;
      continue;
    }

    // Closing tag: </n>
    if (remaining.startsWith('</n>')) {
      const element = stack.pop();
      if (element) {
        currentChildren().push(element);
      }
      position += 4;
      continue;
    }

    // Line break
    if (remaining[0] === '\n') {
      currentChildren().push({type: 'breakline'});
      position += 1;
      continue;
    }

    // Plain text — consume up to the next tag boundary or newline
    const textMatch = remaining.match(/^([^<\n]+)/);
    if (textMatch?.[1]) {
      currentChildren().push({type: 'text', value: textMatch[1]});
      position += textMatch[1].length;
      continue;
    }

    // Unrecognised `<` character (malformed tag) — treat as plain text
    currentChildren().push({type: 'text', value: '<'});
    position += 1;
  }

  // Any unclosed tags (e.g. from a truncated OpenAI response) are flushed to their parent.
  while (stack.length > 0) {
    const element = stack.pop()!;
    currentChildren().push(element);
  }

  return roots;
}
